/**
 * RestTimer — Countdown ring shown between sets
 *
 * Features:
 *   - Web Worker countdown (survives screen lock + tab switches)
 *   - Page Visibility fallback: recalculates on tab resume
 *   - Animated SVG ring that drains over the rest period
 *   - Web Audio API beep cues (no assets)
 *   - navigator.vibrate haptics at 10s warning and completion
 *   - Skip button dismisses early
 */

import { useEffect, useRef, useState, useCallback } from 'react';

// ── Shared AudioContext singleton ─────────────────────────────

let sharedAudioCtx: AudioContext | null = null;

export function primeAudioContext(): void {
  try {
    if (!sharedAudioCtx) sharedAudioCtx = new AudioContext();
    if (sharedAudioCtx.state === 'suspended') sharedAudioCtx.resume();
  } catch { /* unavailable */ }
}

function playBeep(freq = 880, duration = 0.15, gain = 0.4): void {
  if (!sharedAudioCtx) return;
  try {
    const ctx = sharedAudioCtx;
    const osc = ctx.createOscillator();
    const g   = ctx.createGain();
    osc.connect(g);
    g.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.value = freq;
    g.gain.value = gain;
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  } catch { /* ignore */ }
}

function playCompleteCue(): void {
  playBeep(660, 0.1, 0.3);
  setTimeout(() => playBeep(880,  0.1,  0.35), 120);
  setTimeout(() => playBeep(1100, 0.18, 0.4),  240);
}

// ── Component ────────────────────────────────────────────────

interface RestTimerProps {
  durationSeconds: number;
  audioEnabled?: boolean;
  onComplete?: () => void;
  onSkip?: () => void;
}

export default function RestTimer({
  durationSeconds,
  audioEnabled = true,
  onComplete,
  onSkip,
}: RestTimerProps) {
  const [remaining, setRemaining] = useState(durationSeconds);
  const [done, setDone] = useState(false);
  const [audioPrimed, setAudioPrimed] = useState(
    !!(sharedAudioCtx && sharedAudioCtx.state === 'running'),
  );

  const workerRef    = useRef<Worker | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const durationRef  = useRef<number>(durationSeconds);

  const finish = useCallback(() => {
    workerRef.current?.postMessage({ type: 'STOP' });
    if (audioEnabled && audioPrimed) playCompleteCue();
    navigator.vibrate?.([100, 50, 100]);
    setDone(true);
    onComplete?.();
  }, [audioEnabled, audioPrimed, onComplete]);

  const handleSkip = useCallback(() => {
    workerRef.current?.postMessage({ type: 'STOP' });
    setDone(true);
    onSkip?.();
    onComplete?.();
  }, [onComplete, onSkip]);

  // Start worker
  useEffect(() => {
    startTimeRef.current = Date.now();
    durationRef.current  = durationSeconds;

    const worker = new Worker(
      new URL('../../lib/timerWorker.ts', import.meta.url),
      { type: 'module' }
    );
    workerRef.current = worker;

    worker.onmessage = (e: MessageEvent) => {
      const { type, remaining: r } = e.data;
      if (type === 'TICK') {
        setRemaining((prev) => {
          if (r === 10 && prev > 10) navigator.vibrate?.([30]);
          return r;
        });
      }
      if (type === 'DONE') finish();
    };

    worker.postMessage({ type: 'START', durationSeconds });

    return () => {
      worker.postMessage({ type: 'STOP' });
      worker.terminate();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Page Visibility fallback — recalculate on tab resume
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const elapsed   = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const recalc    = Math.max(0, durationRef.current - elapsed);
        setRemaining(recalc);
        if (recalc <= 0) finish();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [finish]);

  if (done) return null;

  const RADIUS = 42;
  const CIRC   = 2 * Math.PI * RADIUS;
  const progress   = remaining / durationSeconds;
  const dashOffset = CIRC * (1 - progress);
  const ringColor  =
    progress > 0.5 ? '#22c55e' :
    progress > 0.2 ? '#f59e0b' :
                     '#ef4444';

  const mins = Math.floor(remaining / 60);
  const secs = (remaining % 60).toString().padStart(2, '0');

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-safe pointer-events-none">
      <button
        className="absolute inset-0 bg-black/40 pointer-events-auto"
        onClick={handleSkip}
        aria-label="Skip rest timer"
      />

      <div className="relative pointer-events-auto mb-24 bg-surface-100 rounded-3xl
                      px-8 py-6 shadow-card-lg flex flex-col items-center gap-4
                      animate-slide-up min-w-[260px]">
        <p className="text-xs text-ink-muted uppercase tracking-widest">Rest</p>

        {/* Countdown ring */}
        <div className="relative w-32 h-32">
          <svg viewBox="0 0 100 100" className="w-32 h-32 -rotate-90">
            <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="#2e2e2e" strokeWidth="6" />
            <circle cx="50" cy="50" r={RADIUS} fill="none"
              stroke={ringColor} strokeWidth="6"
              strokeDasharray={CIRC} strokeDashoffset={dashOffset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.4s linear, stroke 0.5s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold font-mono text-ink leading-none">
              {mins > 0 ? `${mins}:${secs}` : remaining}
            </span>
            {mins === 0 && <span className="text-xs text-ink-muted mt-0.5">sec</span>}
          </div>
        </div>

        {audioEnabled && !audioPrimed && (
          <button
            onClick={() => { primeAudioContext(); setAudioPrimed(true); }}
            className="bg-accent/10 border border-accent/30 rounded-xl px-4 py-2
                       text-xs text-accent font-medium active:scale-95 transition-transform"
          >
            🔔 Tap to enable audio cue
          </button>
        )}

        <button className="btn-ghost text-sm py-2 px-6 w-full" onClick={handleSkip}>
          Skip Rest
        </button>
      </div>
    </div>
  );
}
